/** Log message actor markers store staff ids. Legacy logs may still use `|username|`. */
import { isHiddenLogActorToken, isSystemBotStaffRow } from 'pages/Records/systemBot';
import { formatRecordLogLabel, recordStaffCodeFromFields } from 'pages/Records/recordLogLabel';
import { unwrapLogWithLegacy, wrapLogActorMark } from './logFormat';

export type StaffLike = {
  id?: string | number | null
  username?: string | null
  email?: string | null
  permission?: string | number | null
  photo?: string | null
}

export type StaffMaps = {
  byId: Map<string, StaffLike>
  byUsername: Map<string, StaffLike>
}

export const extractLogActorToken = (message: string): string | null => {
  const tokens = unwrapLogWithLegacy('actor', message || '');
  return tokens[0] || null;
};

export const extractAllLogActorTokens = (message: string): string[] => (
  unwrapLogWithLegacy('actor', message || '')
);

export const buildStaffMaps = (users: StaffLike[] | null | undefined): StaffMaps => {
  const byId = new Map<string, StaffLike>();
  const byUsername = new Map<string, StaffLike>();
  (users || []).forEach((u) => {
    if (isSystemBotStaffRow(u)) return;
    if (u?.id != null && String(u.id) !== '') {
      if (isHiddenLogActorToken(String(u.id))) return;
      byId.set(String(u.id), u);
    }
    if (u?.username) {
      if (isHiddenLogActorToken(u.username)) return;
      byUsername.set(u.username.replace(/\s+/g, '').toLowerCase(), u);
      byUsername.set(u.username.toLowerCase(), u);
    }
  });
  return { byId, byUsername };
};

export const resolveActorUser = (
  token: string | null | undefined,
  maps: StaffMaps,
): StaffLike | null => {
  if (!token || isHiddenLogActorToken(token)) return null;
  const byId = maps.byId.get(String(token));
  if (byId) return byId;
  const key = token.replace(/\s+/g, '').toLowerCase();
  return maps.byUsername.get(key) || maps.byUsername.get(token.toLowerCase()) || null;
};

export const REMOVED_ACTOR_LABEL = 'Unresolved';

type EmployeeCodeSource = {
  kind?: string | null
  name?: string | null
  fields?: Record<string, unknown> | null
}

/** Staff id / username → employeeNumber (email join, else employee name = username). */
export const buildStaffCodeByActorToken = (
  users: StaffLike[] | null | undefined,
  employees: EmployeeCodeSource[] | null | undefined,
): Map<string, string> => {
  const emailToCode = new Map<string, string>();
  const nameToCode = new Map<string, string>();
  (employees || []).forEach((row) => {
    if (String(row.kind || '').trim() !== 'employee') return;
    const code = recordStaffCodeFromFields('employee', row.fields);
    if (!code) return;
    const email = String(row.fields?.email || '').trim().toLowerCase();
    if (email) emailToCode.set(email, code);
    const name = String(row.name || row.fields?.name || '').trim().toLowerCase();
    if (name) nameToCode.set(name, code);
  });
  const map = new Map<string, string>();
  (users || []).forEach((user) => {
    const email = String(user.email || '').trim().toLowerCase();
    const usernameKey = String(user.username || '').trim().toLowerCase();
    const code = (email && emailToCode.get(email))
      || (usernameKey && nameToCode.get(usernameKey))
      || '';
    if (!code) return;
    if (user.id != null && String(user.id) !== '') map.set(String(user.id), code);
    if (!user.username) return;
    map.set(user.username.replace(/\s+/g, '').toLowerCase(), code);
    map.set(user.username.toLowerCase(), code);
  });
  return map;
};

export const resolveActorStaffCode = (
  token: string | null | undefined,
  maps: StaffMaps,
  staffCodeByToken?: Map<string, string> | null,
): string => {
  if (!staffCodeByToken?.size) return '';
  const direct = staffCodeByToken.get(String(token || ''));
  if (direct) return direct;
  const user = resolveActorUser(token, maps);
  if (!user) return '';
  if (user.id != null && String(user.id) !== '') {
    const byId = staffCodeByToken.get(String(user.id));
    if (byId) return byId;
  }
  if (!user.username) return '';
  return staffCodeByToken.get(user.username.replace(/\s+/g, '').toLowerCase())
    || staffCodeByToken.get(user.username.toLowerCase())
    || '';
};

/** True when a following bold wrap is the same staff code we show on the actor chip. */
export const isPersistedActorStaffCodeBold = (
  inner: string | null | undefined,
  staffCode: string | null | undefined,
): boolean => {
  const code = String(staffCode || '').trim().toUpperCase();
  if (!code) return false;
  const raw = String(inner || '').trim().toUpperCase();
  return raw === code || raw === `(${code})`;
};

/** Display name for an actor wrap (id → username; unresolved → Removed). */
export const resolveActorDisplayName = (
  token: string | null | undefined,
  maps: StaffMaps,
  fallback = 'staff',
  staffCodeByToken?: Map<string, string> | null,
): string => {
  if (!token) return fallback;
  const user = resolveActorUser(token, maps);
  if (!user?.username) return REMOVED_ACTOR_LABEL;
  const code = resolveActorStaffCode(token, maps, staffCodeByToken);
  return formatRecordLogLabel(user.username, code);
};

/** Raw token to embed in new log messages (prefer staff id). */
export const logActorToken = (
  staffId: string | number | null | undefined,
  fallbackUsername?: string | null,
  fallback = 'staff',
): string => {
  if (staffId != null && String(staffId) !== '') return String(staffId);
  if (fallbackUsername) return String(fallbackUsername);
  return fallback;
};

export const formatLogActor = (
  staffId: string | number | null | undefined,
  fallbackUsername?: string | null,
  fallback = 'staff',
): string => wrapLogActorMark(logActorToken(staffId, fallbackUsername, fallback));

export type SessionActor = {
  staffId?: string | number | null
  username?: string | null
};

/** Whether a log message mentions this session user (id and/or legacy username). */
export const logMentionsUser = (
  message: string,
  session: SessionActor,
): boolean => {
  const tokens = extractAllLogActorTokens(message);
  if (!tokens.length) return false;
  const staffId = session.staffId != null && String(session.staffId) !== ''
    ? String(session.staffId)
    : '';
  if (staffId && tokens.some((t) => t === staffId)) return true;
  const username = (session.username || '').trim();
  if (username && tokens.some((t) => t.toLowerCase() === username.toLowerCase())) return true;
  return false;
};

/**
 * Actor identity for permission / ownership checks.
 * Prefer `created_by` (staff id), else first actor wrap in the message.
 */
export const getLogActorIdentity = (
  log: { message?: string; created_by?: string | null },
): string | null => {
  if (log.created_by != null && String(log.created_by) !== '') {
    return String(log.created_by);
  }
  return extractLogActorToken(log.message || '');
};
